import { useState } from "react";
import { toast } from "sonner";
import { FormPage, TextField, AreaField, SelectField, SubmitButton, SuccessPanel } from "@/components/forms";
import { api, apiError } from "@/lib/api";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  audience: "",
  preferred_date: "",
  preferred_time: "",
  topic: "",
};

export default function Consultation() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/forms/consultation", form);
      setSent(true);
      toast.success("Consultation requested — we'll confirm shortly.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <FormPage
      testid="consultation-page"
      eyebrow="Schedule a Consultation"
      lines={["Start with a", "conversation."]}
      intro="A consultation is simply time with our team — to ask questions, describe your situation, and see whether Homevia is the right fit. No pressure, no obligation."
      asideTitle="Good to know"
      asideText="Share a preferred date and time and we'll do our best to match it when we confirm."
    >
      {sent ? (
        <SuccessPanel
          testid="consultation-success"
          title="Consultation requested"
          message="Thank you. We'll follow up shortly to confirm a time that works for you."
          onReset={() => {
            setForm(EMPTY);
            setSent(false);
          }}
        />
      ) : (
        <form onSubmit={onSubmit} data-testid="consultation-form" className="space-y-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <TextField id="cs-name" label="Your name" required value={form.name} onChange={set("name")} />
            <SelectField
              id="cs-audience"
              label="I am a"
              value={form.audience}
              onChange={set("audience")}
              options={["Patient", "Family member", "Healthcare professional", "Other"]}
            />
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            <TextField id="cs-email" label="Email" type="email" required value={form.email} onChange={set("email")} />
            <TextField id="cs-phone" label="Phone (optional)" type="tel" value={form.phone} onChange={set("phone")} />
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            <TextField id="cs-date" label="Preferred date" type="date" value={form.preferred_date} onChange={set("preferred_date")} />
            <SelectField
              id="cs-time"
              label="Preferred time"
              value={form.preferred_time}
              onChange={set("preferred_time")}
              options={["Morning", "Afternoon", "Evening", "Flexible"]}
            />
          </div>
          <AreaField
            id="cs-topic"
            label="What would you like to discuss?"
            required
            value={form.topic}
            onChange={set("topic")}
            placeholder="A sentence or two is plenty."
          />
          <SubmitButton loading={loading} testid="consultation-submit-btn">
            Request Consultation
          </SubmitButton>
        </form>
      )}
    </FormPage>
  );
}
